import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { logger } from "@/lib/logger";

const NotifyContext = createContext(null);

const STORAGE_KEY = "feaststar.notify.shown";
const COOKIE_KEY = "feaststar.cookie.consent.v1";

/**
 * Provides a global "Verfügbar wenn die App live ist" modal.
 *
 * Triggers:
 *  - openNotify(source) — programmatic from any component (download buttons,
 *    hero CTAs, origin section, etc.)
 *  - Auto-open on first visit AFTER the cookie banner has been decided,
 *    with a short delay. Persisted via localStorage so it never re-opens
 *    for the same browser.
 */
export function NotifyProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("");

  const openNotify = useCallback((src = "manual") => {
    setSource(src);
    setOpen(true);
  }, []);

  const closeNotify = useCallback(() => setOpen(false), []);

  // Auto-open once on first visit — ONLY after cookie banner has been decided
  useEffect(() => {
    if (typeof window === "undefined") return;

    const tryAutoOpen = () => {
      let alreadyShown = false;
      let cookieDecided = false;
      try {
        alreadyShown = !!window.localStorage.getItem(STORAGE_KEY);
        cookieDecided = !!window.localStorage.getItem(COOKIE_KEY);
      } catch (err) {
        logger.warn("[notify] localStorage read failed:", err);
        alreadyShown = false;
        cookieDecided = false;
      }
      if (alreadyShown || !cookieDecided) return false;

      setSource("auto-first-visit");
      setOpen(true);
      try {
        window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch (err) {
        logger.warn("[notify] localStorage write failed:", err);
      }
      return true;
    };

    // Initial delayed attempt (handles users who already accepted previously)
    const t = setTimeout(tryAutoOpen, 3500);

    // Re-attempt right after the user clicks Accept/Decline
    const onCookieDecided = () => {
      // Slight delay so the banner exit-animation can play first
      setTimeout(tryAutoOpen, 600);
    };
    window.addEventListener("feaststar:cookie-consent", onCookieDecided);
    return () => {
      clearTimeout(t);
      window.removeEventListener("feaststar:cookie-consent", onCookieDecided);
    };
  }, []);

  return (
    <NotifyContext.Provider value={{ open, source, openNotify, closeNotify }}>
      {children}
    </NotifyContext.Provider>
  );
}

export function useNotify() {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error("useNotify must be used within NotifyProvider");
  return ctx;
}
